import { useState, useEffect } from 'preact/hooks';
import Modal from './shared/Modal.jsx';
import Button from './shared/Button.jsx';
import { formatEuros } from '../lib/format.js';

// Een dienst bij een abonnement aan- of uitzetten, zoals de klant hem in het
// product omzette (bij Pand4U). 4U-beheer hoort dat niet van het product; Nick
// neemt het hier over, met de dag waarop het inging.
//
// `target` is { sub, service, on }: het abonnement, de dienst uit
// availableServices en of hij aan moet.
export default function ServiceSwitchModal({ target, onClose, onSaved }) {
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!target) return;
    // Vandaag zoals Nederland het telt; de server rekent ook zo
    setDate(new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(new Date()));
    setError(null);
  }, [target]);

  if (!target) return null;
  const { sub, service, on } = target;
  const requested = sub.status === 'requested';

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/subscriptions/${sub.id}/services/${service.key}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ on, date })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'save_failed');
      }
      onSaved(`${service.label} staat ${on ? 'aan' : 'uit'} voor ${sub.debtorName}.`);
      onClose();
    } catch (err) {
      setError(translate(err.message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`${service.label} ${on ? 'aanzetten' : 'uitzetten'}`}>
      <form onSubmit={submit} class="app-form">
        <p class="muted small">
          {sub.debtorName}, {sub.productName}.{' '}
          {on
            ? `${formatEuros(service.monthlyCents)} per maand excl. btw, elke maand vooruit gefactureerd vanaf de dag hieronder (niet vóór de ingangsdatum van het abonnement).`
            : 'De maand waarin de dienst uitgaat is al gefactureerd en blijft staan; daarna komt hij niet meer op een factuur.'}
        </p>
        {requested ? (
          <p class="muted small">
            {on
              ? 'Het abonnement wacht nog op goedkeuring: de dienst begint met het abonnement.'
              : 'Het abonnement wacht nog op goedkeuring: de dienst verdwijnt gewoon.'}
          </p>
        ) : (
          <label>
            <span>{on ? 'Aan vanaf' : 'Uit vanaf'}</span>
            <input type="date" value={date} onInput={e => setDate(e.currentTarget.value)} required />
          </label>
        )}
        {error && <div class="form-error">{error}</div>}
        <div class="form-actions">
          <Button variant="ghost" type="button" onClick={onClose}>Annuleren</Button>
          <Button type="submit" disabled={busy || (!requested && !date)}>
            {busy ? 'Bezig…' : on ? 'Aanzetten' : 'Uitzetten'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function translate(code) {
  const messages = {
    invalid_input: 'Kies een geldige datum.',
    invalid_date: 'Kies een geldige datum.',
    unknown_service: 'Deze dienst kent het product niet.',
    subscription_canceled: 'Dit abonnement is opgezegd.',
    not_found: 'Dit abonnement bestaat niet meer — herlaad de pagina.',
    save_failed: 'Opslaan is mislukt.'
  };
  return messages[code] || 'Er ging iets mis. Probeer opnieuw.';
}
